"use client";
import { useDropzone, FileRejection } from "react-dropzone";
import { Flex, Text, Image as ChakraImage, Box, IconButton, useToast } from "@chakra-ui/react";
import { LuImagePlus } from "react-icons/lu";
import { DeleteIcon } from "@chakra-ui/icons";

type FileWithPreview = {
  file: File;
  preview: string;
};

type DropzoneProps = {
  files: FileWithPreview[];
  setFiles: React.Dispatch<React.SetStateAction<FileWithPreview[]>>;
  useFlux: boolean;
};

const ACCEPTED_IMAGE_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
  'image/heic': ['.heic'],
  'application/octet-stream': ['.jpg', '.jpeg', '.png', '.webp', '.heic'] // parse MIME octet-stream into accepted format because of mobile.

} 

const Dropzone: React.FC<DropzoneProps> = ({ files, setFiles, useFlux }) => {
  const toast = useToast();

  const isValidFileType = (file: File) => {
    // If it's octet-stream check the file extension, because of mobile issue where images can be sent as application/octet-stream and will be translated into .bin on BE
    if (file.type === 'application/octet-stream') {
      const extension = file.name.toLowerCase().split('.').pop();
      return extension && ['.jpg', '.jpeg', '.png', '.webp', '.heic']
        .map(ext => ext.replace('.', ''))
        .includes(extension);
    }
    // Otherwise check MIME type
    return Object.keys(ACCEPTED_IMAGE_TYPES).includes(file.type);
  };

  const checkDimensions = (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        if (img.width < 512 || img.height < 512) {
          resolve(false);
        } else {
          resolve(true);
        }
      };
      img.onerror = () => resolve(false);
      img.src = URL.createObjectURL(file);
    });
  };

  const onDrop = async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
    const validFiles: FileWithPreview[] = [];
    const dimensionCheckPromises: Promise<void>[] = [];
  
    // Check each accepted file
    for (const file of acceptedFiles) {
      // Strict file type checking
      if (!isValidFileType(file)) {
        toast({
          title: "Invalid File Type",
          description: `${file.name} must be a JPEG, PNG, WEBP, or HEIC file.`,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        continue;
      }

      // Size check for FLUX
      if (useFlux && file.size > 5 * 1024 * 1024) {
        toast({
          title: "File Too Large",
          description: `${file.name} exceeds the 5MB size limit for Flux mode.`,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        continue;
      }
  
      const promise = checkDimensions(file).then((isValid) => {
        if (!isValid) {
          toast({
            title: "Image Too Small",
            description: `${file.name} must be at least 512x512 pixels.`,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        } else {
          validFiles.push({
            file,
            preview: URL.createObjectURL(file),
          });
        }
      });
  
      dimensionCheckPromises.push(promise);
    }
  
    await Promise.all(dimensionCheckPromises);
    setFiles((prevFiles) => [...prevFiles, ...validFiles]);
  };

  

  const removeFile = (name: string) => {
    setFiles((prevFiles) => {
      const newFiles = prevFiles.filter((file) => file.file.name !== name);
      const removedFile = prevFiles.find((file) => file.file.name === name);
      if (removedFile) URL.revokeObjectURL(removedFile.preview);
      return newFiles;
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: useFlux ? {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png']
    } : ACCEPTED_IMAGE_TYPES,
    maxSize: useFlux ? 5 * 1024 * 1024 : 15 * 1024 * 1024
  });

  const thumbs = files.map((file) => (
    <Box
      key={file.file.name}
      p="2"
      display="inline-block"
      position="relative"
      borderRadius="lg"
      overflow="hidden"
    >
      <ChakraImage objectFit="cover" src={file.preview} alt={file.file.name} w="100px" h="100px" />
      <IconButton
        aria-label="Delete image"
        icon={<DeleteIcon />}
        size="sm"
        colorScheme="red"
        position="absolute"
        right="2"
        bottom="2"
        onClick={(e) => {
          e.stopPropagation();
          removeFile(file.file.name);
        }}
      />
    </Box>
  ));

  return (
    <div
      {...getRootProps()}
      style={{ marginTop: "20px", border: "2px dashed gray", padding: "20px", textAlign: "center" }}
    >
      <input {...getInputProps()} />
      <Flex minH="250px" direction="column" w="100%" align="center" justify="center">
        {isDragActive && !files.length ? (
          <Text> Drop your files here!</Text>
        ) : (
          <>
            <LuImagePlus size={35} />
            <Text mt="3px" color="#1D71F9" fontWeight={700}>
              {" "}
              Upload files or drag & drop
            </Text>
            {useFlux ? "PNG and JPEG up to 5MB" : "PNG, JPG, WEBP, and HEIC up to 15MB"}
            <Text fontWeight={700} color="#E20FCE">
              Total size limited to 100MB
            </Text>
            <aside style={{ display: "flex", flexWrap: "wrap", marginTop: "16px" }}>
              {thumbs}
            </aside>
            <Text mt="50px" fontWeight={700} color="#E20FCE">
              {files.length
                ? `${files.length} images uploaded. ${
                    files.length >= 5 ? "Ready to go" : `Upload ${5 - files.length} more `
                  }`
                : "Upload between 5 to 30 training images"}
            </Text>
          </>
        )}
      </Flex>
    </div>
  );
};

export default Dropzone;
